<template>
  <div class="page-card">
    <div class="section-header">
      <div class="section-header-left">
        <div class="section-title">医生列表</div>
        <div class="search-bar">
          <input
            v-model.trim="searchName"
            class="search-input"
            placeholder="医生名称"
            @keyup.enter="handleSearch"
          />
          <input
            v-model.trim="searchPhone"
            class="search-input"
            placeholder="联系方式"
            @keyup.enter="handleSearch"
          />
          <button class="search-btn search-btn-reset" type="button" @click="resetSearch">
            重置
          </button>
          <button
            class="search-btn search-btn-query"
            type="button"
            :disabled="listLoading"
            @click="handleSearch"
          >
            查询
          </button>
        </div>
      </div>
    </div>

    <div v-if="listLoading" class="list-loading">加载中...</div>
    <template v-else>
      <table class="doctor-table">
        <thead>
          <tr>
            <th>医生名称</th>
            <th>联系方式</th>
            <th class="col-action">操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!rows.length">
            <td colspan="3" class="empty-cell">暂无数据</td>
          </tr>
          <tr v-for="(doctor, index) in rows" :key="getDoctorRowKey(doctor, index)">
            <td>
              <input
                v-if="isDoctorEditing(doctor, index)"
                v-model.trim="doctorEditDraft.realName"
                class="doctor-inline-input"
                placeholder="请输入医生名称"
              />
              <span v-else>{{ doctor.realName || "-" }}</span>
            </td>
            <td>
              <input
                v-if="isDoctorEditing(doctor, index)"
                v-model.trim="doctorEditDraft.phone"
                class="doctor-inline-input"
                placeholder="请输入联系方式"
              />
              <span v-else>{{ doctor.phone || "-" }}</span>
            </td>
            <td class="col-action">
              <div class="action-btns">
                <template v-if="isDoctorEditing(doctor, index)">
                  <button
                    type="button"
                    class="mini-btn mini-btn-confirm"
                    :disabled="savingDoctorEditKey === getDoctorRowKey(doctor, index)"
                    @click="confirmEditDoctor(doctor, index)"
                  >
                    {{
                      savingDoctorEditKey === getDoctorRowKey(doctor, index)
                        ? "提交中..."
                        : "确认"
                    }}
                  </button>
                  <button type="button" class="mini-btn mini-btn-cancel" @click="cancelEditDoctor">
                    取消
                  </button>
                </template>
                <template v-else>
                  <button
                    class="action-btn action-btn-relate"
                    type="button"
                    :disabled="isDoctorListEditing"
                    @click="openRelateHospital(doctor)"
                  >
                    关联医院
                  </button>
                  <button
                    class="action-btn action-btn-edit"
                    type="button"
                    :disabled="isDoctorListEditing"
                    @click="startEditDoctor(doctor, index)"
                  >
                    修改
                  </button>
                </template>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <el-pagination
        v-if="total > 0"
        class="doctor-pagination"
        small
        layout="total, prev, pager, next"
        :current-page="pageNum"
        :page-size="pageSize"
        :total="total"
        @current-change="onPageChange"
      />
    </template>

    <!-- 关联医院弹框 -->
    <div
      v-if="showRelateModal"
      class="modal-overlay"
      @click.self="closeRelateModal"
    >
      <div class="modal modal-relate-hospital">
        <div class="modal-header">
          <span class="modal-title">
            关联医院
            <span v-if="relateDoctor.realName" class="modal-subtitle">
              - {{ relateDoctor.realName }}
            </span>
          </span>
          <span class="modal-close" @click="closeRelateModal">×</span>
        </div>
        <div class="modal-body relate-modal-body">
          <div class="relate-search-form">
            <div class="relate-search-item">
              <label class="relate-search-label">门诊名称</label>
              <input
                v-model.trim="hospitalSearchName"
                class="relate-search-input"
                placeholder="请输入门诊名称"
                @keyup.enter="handleHospitalSearch"
              />
            </div>
            <div class="relate-search-item">
              <label class="relate-search-label">门诊编码</label>
              <input
                v-model.trim="hospitalSearchCode"
                class="relate-search-input"
                placeholder="请输入门诊编码"
                @keyup.enter="handleHospitalSearch"
              />
            </div>
            <div class="relate-search-actions">
              <button
                class="search-btn search-btn-reset"
                type="button"
                @click="resetHospitalSearch"
              >
                重置
              </button>
              <button
                class="search-btn search-btn-query"
                type="button"
                :disabled="hospitalListLoading"
                @click="handleHospitalSearch"
              >
                查询
              </button>
            </div>
          </div>

          <div class="relate-panels" :style="{ minHeight: hospitalRelatePanelHeight }">
            <div
              class="relate-selected-panel"
              :style="{ height: hospitalRelatePanelHeight }"
            >
              <div class="relate-selected-header">
                <button
                  class="relate-clear-link"
                  type="button"
                  :disabled="!selectedHospitalList.length"
                  @click="clearAllSelectedHospitals"
                >
                  清空全部
                </button>
                <span class="relate-selected-count">共{{ selectedHospitalList.length }}条</span>
              </div>
              <div
                class="relate-selected-list"
                :style="{ height: hospitalRelateSelectedListHeight }"
              >
                <div v-if="!selectedHospitalList.length" class="relate-selected-empty">
                  暂未选择医院
                </div>
                <div
                  v-for="hospital in selectedHospitalList"
                  :key="'selected-' + hospital.hospitalId"
                  class="relate-selected-tag"
                >
                  <el-tooltip
                    :content="getSelectedHospitalTooltip(hospital)"
                    placement="top"
                    :open-delay="300"
                  >
                    <span class="relate-selected-tag-text">{{ hospital.hospitalName }}</span>
                  </el-tooltip>
                  <button
                    class="relate-selected-tag-remove"
                    type="button"
                    @click="removeSelectedHospital(hospital.hospitalId)"
                  >
                    ×
                  </button>
                </div>
              </div>
            </div>

            <div
              class="relate-table-panel"
              :style="{ minHeight: hospitalRelatePanelHeight }"
            >
              <div
                v-if="hospitalListLoading"
                class="relate-list-loading"
                :style="{ height: hospitalRelateTableHeight }"
              >
                加载中...
              </div>
              <template v-else>
                <div
                  class="relate-table-wrap"
                  :style="{ height: hospitalRelateTableHeight }"
                >
                  <table class="relate-hospital-table">
                    <thead>
                      <tr>
                        <th class="col-select">
                          <input
                            type="checkbox"
                            :checked="isCurrentPageAllSelected"
                            :indeterminate.prop="isCurrentPageIndeterminate"
                            @change="toggleSelectAllCurrentPage"
                          />
                        </th>
                        <th>门诊编码</th>
                        <th>门诊名称</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-if="!hospitalRows.length">
                        <td colspan="3" class="empty-cell">暂无医院</td>
                      </tr>
                      <tr
                        v-for="hospital in hospitalRows"
                        :key="hospital.hospitalId || hospital.hospitalCode"
                        :class="{ 'row-selected': isHospitalSelected(hospital.hospitalId) }"
                        @click="toggleHospitalSelect(hospital)"
                      >
                        <td class="col-select" @click.stop>
                          <input
                            type="checkbox"
                            :checked="isHospitalSelected(hospital.hospitalId)"
                            @change="toggleHospitalSelect(hospital)"
                          />
                        </td>
                        <td>{{ hospital.hospitalCode || "-" }}</td>
                        <td>{{ hospital.hospitalName || "-" }}</td>
                      </tr>
                      <tr
                        v-for="n in emptyHospitalRowCount"
                        :key="'hospital-placeholder-' + n"
                        class="relate-placeholder-row"
                        aria-hidden="true"
                      >
                        <td class="col-select">&nbsp;</td>
                        <td>&nbsp;</td>
                        <td>&nbsp;</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <el-pagination
                  v-if="hospitalTotal > 0"
                  class="relate-pagination"
                  small
                  layout="total, prev, pager, next, jumper"
                  :current-page="hospitalPageNum"
                  :page-size="hospitalPageSize"
                  :total="hospitalTotal"
                  @current-change="onHospitalPageChange"
                />
              </template>
            </div>
          </div>
        </div>
        <div class="modal-footer modal-footer-center">
          <button class="footer-btn footer-btn-cancel" type="button" @click="closeRelateModal">
            取消
          </button>
          <button
            class="footer-btn footer-btn-save"
            type="button"
            :disabled="savingRelate || !selectedHospitalList.length"
            @click="saveRelateHospital"
          >
            {{ savingRelate ? "提交中..." : "确认" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  listAllDoctors,
  relateDoctorHospital,
  updateDoctor,
  listRelatedHospitals,
} from "@/api/doctor";
import { listAllHospitals } from "@/api/hospital";

const SUCCESS_CODE = 20000;
const HOSPITAL_RELATE_PAGE_SIZE = 10;
const HOSPITAL_RELATE_ROW_HEIGHT = 44;
const HOSPITAL_RELATE_PAGINATION_GAP = 24;
const HOSPITAL_RELATE_PAGINATION_HEIGHT = 32;
const HOSPITAL_RELATE_SELECTED_HEADER_HEIGHT = 40;

export default {
  name: "DoctorList",
  data() {
    return {
      listLoading: false,
      savingDoctorEditKey: null,
      savingRelate: false,
      showRelateModal: false,
      pageNum: 1,
      pageSize: 10,
      total: 0,
      searchName: "",
      searchPhone: "",
      rows: [],
      editingDoctorKey: null,
      doctorEditDraft: {
        realName: "",
        phone: "",
      },
      relateDoctor: {
        doctorId: "",
        realName: "",
      },
      selectedHospitalMap: {},
      hospitalSearchCode: "",
      hospitalSearchName: "",
      hospitalListLoading: false,
      hospitalPageNum: 1,
      hospitalPageSize: HOSPITAL_RELATE_PAGE_SIZE,
      hospitalTotal: 0,
      hospitalRows: [],
    };
  },
  computed: {
    hospitalRelateTableHeight() {
      return `${HOSPITAL_RELATE_ROW_HEIGHT * (HOSPITAL_RELATE_PAGE_SIZE + 1)}px`;
    },
    hospitalRelatePanelHeight() {
      const tableHeight = HOSPITAL_RELATE_ROW_HEIGHT * (HOSPITAL_RELATE_PAGE_SIZE + 1);
      return `${tableHeight + HOSPITAL_RELATE_PAGINATION_GAP + HOSPITAL_RELATE_PAGINATION_HEIGHT}px`;
    },
    hospitalRelateSelectedListHeight() {
      const panelHeight =
        HOSPITAL_RELATE_ROW_HEIGHT * (HOSPITAL_RELATE_PAGE_SIZE + 1) +
        HOSPITAL_RELATE_PAGINATION_GAP +
        HOSPITAL_RELATE_PAGINATION_HEIGHT;
      return `${panelHeight - HOSPITAL_RELATE_SELECTED_HEADER_HEIGHT}px`;
    },
    emptyHospitalRowCount() {
      if (this.hospitalListLoading) return 0;
      if (!this.hospitalRows.length) {
        return HOSPITAL_RELATE_PAGE_SIZE - 1;
      }
      return Math.max(0, this.hospitalPageSize - this.hospitalRows.length);
    },
    isDoctorListEditing() {
      return this.editingDoctorKey !== null;
    },
    selectedHospitalList() {
      return Object.values(this.selectedHospitalMap);
    },
    currentPageSelectedCount() {
      return (this.hospitalRows || []).filter((item) =>
        this.isHospitalSelected(item.hospitalId)
      ).length;
    },
    isCurrentPageAllSelected() {
      const rows = this.hospitalRows || [];
      if (!rows.length) return false;
      return rows.every((item) => this.isHospitalSelected(item.hospitalId));
    },
    isCurrentPageIndeterminate() {
      const count = this.currentPageSelectedCount;
      const total = (this.hospitalRows || []).length;
      return count > 0 && count < total;
    },
  },
  mounted() {
    this.fetchDoctorList();
  },
  methods: {
    showMessage(type, text) {
      this.$message({ message: text, type });
    },
    isBizError(res) {
      return res && res.code != null && Number(res.code) !== SUCCESS_CODE;
    },
    resolveListPayload(data) {
      if (Array.isArray(data)) {
        return { list: data, total: data.length };
      }
      if (!data || typeof data !== "object") {
        return { list: [], total: 0 };
      }
      const list = data.list || data.records || data.rows || [];
      const total = Number(data.total ?? list.length) || 0;
      return { list: Array.isArray(list) ? list : [], total };
    },
    mapDoctorRow(item) {
      if (!item || typeof item !== "object") return null;
      const doctorId = String(item.doctorId ?? item.id ?? "").trim();
      const realName = String(item.realName ?? item.doctorName ?? "").trim();
      const phone = String(item.phone ?? "").trim();
      if (!doctorId) return null;
      return { doctorId, realName, phone };
    },
    mapDoctorRows(list) {
      if (!Array.isArray(list)) return [];
      return list.map((item) => this.mapDoctorRow(item)).filter(Boolean);
    },
    async fetchDoctorList() {
      this.listLoading = true;
      try {
        const res = await listAllDoctors({
          pageNum: this.pageNum,
          pageSize: this.pageSize,
          realName: this.searchName,
          phone: this.searchPhone,
        });
        if (this.isBizError(res)) {
          this.showMessage("error", res.msg || "医生列表加载失败");
          this.rows = [];
          this.total = 0;
          return;
        }
        const payload = this.resolveListPayload(res && res.data);
        this.rows = this.mapDoctorRows(payload.list);
        this.total = payload.total;
      } catch (error) {
        const msg =
          (error && error.msg) || error.message || "医生列表加载失败，请稍后重试";
        this.showMessage("error", msg);
        this.rows = [];
        this.total = 0;
      } finally {
        this.listLoading = false;
      }
    },
    onPageChange(page) {
      this.pageNum = page;
      this.fetchDoctorList();
    },
    handleSearch() {
      this.pageNum = 1;
      this.fetchDoctorList();
    },
    resetSearch() {
      this.searchName = "";
      this.searchPhone = "";
      this.pageNum = 1;
      this.fetchDoctorList();
    },
    getDoctorRowKey(doctor, index) {
      return doctor.doctorId || `row-${index}`;
    },
    isDoctorEditing(doctor, index) {
      return this.editingDoctorKey === this.getDoctorRowKey(doctor, index);
    },
    startEditDoctor(doctor, index) {
      if (this.isDoctorListEditing) return;
      if (!doctor.doctorId) {
        this.showMessage("warning", "医生信息无效");
        return;
      }
      this.editingDoctorKey = this.getDoctorRowKey(doctor, index);
      this.doctorEditDraft = {
        realName: doctor.realName || "",
        phone: doctor.phone || "",
      };
    },
    cancelEditDoctor() {
      this.editingDoctorKey = null;
      this.savingDoctorEditKey = null;
      this.doctorEditDraft = { realName: "", phone: "" };
    },
    async confirmEditDoctor(doctor, index) {
      const rowKey = this.getDoctorRowKey(doctor, index);
      const { realName, phone } = this.doctorEditDraft;
      if (!doctor.doctorId) {
        this.showMessage("warning", "医生信息无效");
        return;
      }
      if (!realName) {
        this.showMessage("warning", "请填写医生名称");
        return;
      }
      if (!phone) {
        this.showMessage("warning", "请填写联系方式");
        return;
      }

      this.savingDoctorEditKey = rowKey;
      try {
        const res = await updateDoctor({
          doctorId: doctor.doctorId,
          realName,
          phone,
        });
        if (this.isBizError(res)) {
          this.showMessage("error", res.msg || "医生修改失败");
          return;
        }
        this.showMessage("success", (res && res.msg) || "医生修改成功");
        this.cancelEditDoctor();
        await this.fetchDoctorList();
      } catch (error) {
        const msg =
          (error && error.msg) || error.message || "医生修改失败，请稍后重试";
        this.showMessage("error", msg);
      } finally {
        this.savingDoctorEditKey = null;
      }
    },
    async openRelateHospital(doctor) {
      if (!doctor || !doctor.doctorId) {
        this.showMessage("warning", "医生信息无效");
        return;
      }
      this.relateDoctor = {
        doctorId: doctor.doctorId,
        realName: doctor.realName || "",
      };
      this.selectedHospitalMap = {};
      this.hospitalSearchCode = "";
      this.hospitalSearchName = "";
      this.hospitalPageNum = 1;
      this.showRelateModal = true;
      await Promise.all([
        this.fetchRelatedHospitals(doctor.doctorId),
        this.fetchHospitalList(),
      ]);
    },
    fillSelectedHospitalsFromList(list) {
      const nextMap = {};
      (list || [])
        .map((item) => this.mapHospitalRow(item))
        .filter(Boolean)
        .forEach((hospital) => {
          nextMap[hospital.hospitalId] = hospital;
        });
      this.selectedHospitalMap = nextMap;
    },
    async fetchRelatedHospitals(doctorId) {
      try {
        const res = await listRelatedHospitals({ doctorId });
        if (this.isBizError(res)) {
          this.showMessage("error", res.msg || "已关联医院加载失败");
          this.selectedHospitalMap = {};
          return;
        }
        const payload = this.resolveListPayload(res && res.data);
        this.fillSelectedHospitalsFromList(payload.list);
      } catch (error) {
        const msg =
          (error && error.msg) || error.message || "已关联医院加载失败，请稍后重试";
        this.showMessage("error", msg);
        this.selectedHospitalMap = {};
      }
    },
    closeRelateModal() {
      this.showRelateModal = false;
      this.relateDoctor = { doctorId: "", realName: "" };
      this.selectedHospitalMap = {};
      this.hospitalRows = [];
      this.hospitalTotal = 0;
    },
    isHospitalSelected(hospitalId) {
      if (!hospitalId) return false;
      return Boolean(this.selectedHospitalMap[String(hospitalId)]);
    },
    addSelectedHospital(hospital) {
      if (!hospital || !hospital.hospitalId) return;
      const hospitalId = String(hospital.hospitalId);
      this.$set(this.selectedHospitalMap, hospitalId, {
        hospitalId,
        hospitalCode: hospital.hospitalCode || "",
        hospitalName: hospital.hospitalName || hospital.hospitalCode || hospitalId,
      });
    },
    removeSelectedHospital(hospitalId) {
      if (!hospitalId) return;
      this.$delete(this.selectedHospitalMap, String(hospitalId));
    },
    toggleHospitalSelect(hospital) {
      if (!hospital || !hospital.hospitalId) return;
      const hospitalId = String(hospital.hospitalId);
      if (this.isHospitalSelected(hospitalId)) {
        this.removeSelectedHospital(hospitalId);
      } else {
        this.addSelectedHospital(hospital);
      }
    },
    clearAllSelectedHospitals() {
      this.selectedHospitalMap = {};
    },
    toggleSelectAllCurrentPage(event) {
      const checked = event && event.target ? event.target.checked : !this.isCurrentPageAllSelected;
      (this.hospitalRows || []).forEach((hospital) => {
        if (checked) {
          this.addSelectedHospital(hospital);
        } else {
          this.removeSelectedHospital(hospital.hospitalId);
        }
      });
    },
    resetHospitalSearch() {
      this.hospitalSearchCode = "";
      this.hospitalSearchName = "";
      this.hospitalPageNum = 1;
      this.fetchHospitalList();
    },
    getSelectedHospitalTooltip(hospital) {
      if (!hospital) return "";
      const name = hospital.hospitalName || "";
      const code = hospital.hospitalCode || "";
      if (name && code) return `${name}（${code}）`;
      return name || code || hospital.hospitalId || "";
    },
    mapHospitalRow(item) {
      if (!item || typeof item !== "object") return null;
      const hospitalId = String(item.hospitalId ?? "").trim();
      if (!hospitalId) return null;
      return {
        hospitalId,
        hospitalCode: String(item.hospitalCode ?? "").trim(),
        hospitalName: String(item.hospitalName ?? "").trim(),
      };
    },
    async fetchHospitalList() {
      this.hospitalListLoading = true;
      try {
        const res = await listAllHospitals({
          pageNum: this.hospitalPageNum,
          pageSize: this.hospitalPageSize,
          hospitalCode: this.hospitalSearchCode,
          hospitalName: this.hospitalSearchName,
        });
        if (this.isBizError(res)) {
          this.showMessage("error", res.msg || "医院列表加载失败");
          this.hospitalRows = [];
          this.hospitalTotal = 0;
          return;
        }
        const payload = this.resolveListPayload(res && res.data);
        this.hospitalRows = (payload.list || [])
          .map((item) => this.mapHospitalRow(item))
          .filter(Boolean);
        this.hospitalTotal = payload.total;
      } catch (error) {
        const msg =
          (error && error.msg) || error.message || "医院列表加载失败，请稍后重试";
        this.showMessage("error", msg);
        this.hospitalRows = [];
        this.hospitalTotal = 0;
      } finally {
        this.hospitalListLoading = false;
      }
    },
    handleHospitalSearch() {
      this.hospitalPageNum = 1;
      this.fetchHospitalList();
    },
    onHospitalPageChange(page) {
      this.hospitalPageNum = page;
      this.fetchHospitalList();
    },
    async saveRelateHospital() {
      const { doctorId } = this.relateDoctor;
      if (!doctorId) {
        this.showMessage("warning", "医生信息无效");
        return;
      }
      const hospitalIds = this.selectedHospitalList.map((item) => item.hospitalId);
      if (!hospitalIds.length) {
        this.showMessage("warning", "请选择医院");
        return;
      }

      this.savingRelate = true;
      try {
        const res = await relateDoctorHospital({
          doctorId,
          hospitalIds,
        });
        if (this.isBizError(res)) {
          this.showMessage("error", res.msg || "关联医院失败");
          return;
        }
        this.showMessage("success", (res && res.msg) || "关联医院成功");
        this.closeRelateModal();
      } catch (error) {
        const msg =
          (error && error.msg) || error.message || "关联医院失败，请稍后重试";
        this.showMessage("error", msg);
      } finally {
        this.savingRelate = false;
      }
    },
  },
};
</script>

<style scoped>
.page-card {
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  gap: 12px;
  flex-wrap: wrap;
}

.section-header-left {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
  min-width: 0;
  flex-wrap: wrap;
}

.section-title {
  font-size: 18px;
  color: #333;
  margin: 0;
  font-weight: 600;
  white-space: nowrap;
}

.search-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.search-input {
  width: 140px;
  height: 32px;
  padding: 0 10px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 14px;
  color: #333;
  box-sizing: border-box;
}

.search-input:focus {
  outline: none;
  border-color: #409eff;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.15);
}

.search-btn {
  height: 32px;
  padding: 0 14px;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  white-space: nowrap;
}

.search-btn-reset {
  background: #fff;
  color: #5c6b7f;
  border: 1px solid #d9d9d9;
}

.search-btn-reset:hover {
  color: #409eff;
  border-color: #409eff;
}

.search-btn-query {
  background: #409eff;
  color: #fff;
  border: 1px solid #409eff;
}

.search-btn-query:hover:not(:disabled) {
  background: #096dd9;
  border-color: #096dd9;
}

.search-btn-query:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.list-loading,
.empty-cell {
  padding: 40px 0;
  text-align: center;
  color: #999;
  font-size: 14px;
}

.doctor-table {
  width: 100%;
  border-collapse: collapse;
  border: 1px solid #eef1f6;
}

.doctor-table th,
.doctor-table td {
  border-bottom: 1px solid #eef1f6;
  padding: 12px 14px;
  font-size: 14px;
  color: #3f4a5d;
  vertical-align: middle;
  text-align: left;
}

.doctor-table th {
  background: #f8f9fc;
  color: #616f84;
  font-weight: 600;
}

.doctor-table tbody tr:hover td {
  background: #f0f7ff;
}

.doctor-table .col-action {
  width: 220px;
  text-align: center;
}

.action-btns {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  flex-wrap: wrap;
}

.action-btn {
  border-radius: 4px;
  padding: 5px 10px;
  font-size: 12px;
  cursor: pointer;
  white-space: nowrap;
}

.action-btn-relate {
  background: #409eff;
  color: #fff;
  border: 1px solid #409eff;
}

.action-btn-relate:hover:not(:disabled) {
  background: #096dd9;
  border-color: #096dd9;
}

.action-btn-edit {
  background: #fff;
  color: #409eff;
  border: 1px solid #409eff;
}

.action-btn-edit:hover:not(:disabled) {
  color: #096dd9;
  border-color: #096dd9;
}

.action-btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.doctor-inline-input {
  width: 100%;
  min-width: 120px;
  padding: 6px 10px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 14px;
  box-sizing: border-box;
  color: #333;
}

.doctor-inline-input:focus {
  outline: none;
  border-color: #409eff;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.15);
}

.mini-btn {
  border: none;
  border-radius: 4px;
  padding: 4px 10px;
  font-size: 12px;
  cursor: pointer;
  margin-right: 6px;
}

.mini-btn:last-child {
  margin-right: 0;
}

.mini-btn-confirm {
  background: #52c41a;
  color: #fff;
  border: 1px solid #52c41a;
}

.mini-btn-confirm:hover:not(:disabled) {
  background: #389e0d;
}

.mini-btn-confirm:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.mini-btn-cancel {
  background: #fff;
  color: #666;
  border: 1px solid #d9d9d9;
}

.doctor-pagination {
  margin-top: 16px;
  text-align: right;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.15);
}

.modal-relate-hospital {
  width: 920px;
  max-width: calc(100% - 32px);
  display: flex;
  flex-direction: column;
}

.modal-header {
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
}

.modal-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.modal-subtitle {
  font-size: 14px;
  font-weight: 400;
  color: #666;
}

.modal-close {
  cursor: pointer;
  font-size: 20px;
  color: #999;
  line-height: 1;
}

.modal-body {
  padding: 20px;
  overflow-y: auto;
  flex: 1;
}

.relate-modal-body {
  overflow: visible;
}

.relate-search-form {
  display: flex;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}

.relate-search-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.relate-search-label {
  font-size: 14px;
  color: #333;
  line-height: 32px;
  white-space: nowrap;
  flex-shrink: 0;
}

.relate-search-input {
  width: 180px;
  height: 32px;
  padding: 0 10px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 14px;
  color: #333;
  box-sizing: border-box;
}

.relate-search-input:focus {
  outline: none;
  border-color: #409eff;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.15);
}

.relate-search-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}

.relate-panels {
  display: flex;
  gap: 16px;
  align-items: stretch;
}

.relate-selected-panel {
  width: 220px;
  flex-shrink: 0;
  border: 1px solid #e8ebf0;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  background: #fff;
  overflow: hidden;
}

.relate-selected-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 40px;
  padding: 0 12px;
  border-bottom: 1px solid #eef1f6;
  background: #fafbfc;
  box-sizing: border-box;
  flex-shrink: 0;
}

.relate-clear-link {
  border: none;
  background: none;
  color: #409eff;
  font-size: 13px;
  line-height: 1;
  padding: 0;
  cursor: pointer;
}

.relate-clear-link:disabled {
  color: #bfbfbf;
  cursor: not-allowed;
}

.relate-selected-count {
  font-size: 13px;
  color: #666;
  line-height: 1;
}

.relate-selected-list {
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  box-sizing: border-box;
  background: #fafbfc;
}

.relate-selected-empty {
  color: #999;
  font-size: 13px;
  text-align: center;
  padding: 24px 8px;
}

.relate-selected-tag {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 10px;
  background: #fff;
  border: 1px solid #d9e8ff;
  border-radius: 4px;
}

.relate-selected-tag-text {
  display: inline-block;
  max-width: 156px;
  font-size: 13px;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
  cursor: default;
}

.relate-selected-tag-remove {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  border: none;
  background: transparent;
  color: #999;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  padding: 0;
}

.relate-selected-tag-remove:hover {
  color: #ff4d4f;
}

.relate-table-panel {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.relate-table-wrap {
  flex-shrink: 0;
  overflow: visible;
  border: 1px solid #e8ebf0;
  border-radius: 4px;
  background: #fff;
  box-sizing: border-box;
}

.relate-list-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e8ebf0;
  border-radius: 4px;
  color: #666;
  font-size: 14px;
  box-sizing: border-box;
}

.relate-hospital-table {
  width: 100%;
  border-collapse: collapse;
}

.relate-hospital-table th,
.relate-hospital-table td {
  border-bottom: 1px solid #eef1f6;
  height: 44px;
  min-height: 44px;
  padding: 10px 12px;
  font-size: 14px;
  line-height: 22px;
  color: #3f4a5d;
  text-align: left;
  box-sizing: border-box;
  vertical-align: middle;
}

.relate-hospital-table tbody tr:last-child td {
  border-bottom: none;
}

.relate-hospital-table .empty-cell {
  padding: 9px 12px;
  line-height: 22px;
}

.relate-hospital-table .col-select input[type="checkbox"] {
  margin: 0;
  vertical-align: middle;
}

.relate-placeholder-row td {
  color: transparent;
  user-select: none;
}

.relate-hospital-table th {
  background: #f8f9fc;
  color: #616f84;
  font-weight: 600;
}

.relate-hospital-table .col-select {
  width: 56px;
  text-align: center;
}

.relate-hospital-table tbody tr {
  cursor: pointer;
}

.relate-hospital-table tbody tr:hover td {
  background: #f0f7ff;
}

.relate-hospital-table tbody tr.row-selected td {
  background: #e6f7ff;
}

.relate-pagination {
  margin-top: 24px;
  padding-top: 0;
  text-align: right;
  flex-shrink: 0;
}

.modal-footer {
  padding: 12px 20px 16px;
  border-top: 1px solid #f0f0f0;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  flex-shrink: 0;
}

.modal-footer-center {
  justify-content: center;
}

.footer-btn {
  min-width: 72px;
  height: 32px;
  padding: 0 16px;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
}

.footer-btn-cancel {
  background: #fff;
  color: #333;
  border: 1px solid #d9d9d9;
}

.footer-btn-save {
  background: #409eff;
  color: #fff;
  border: 1px solid #409eff;
}

.footer-btn-save:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}
</style>
